import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import { useNextFixtureProbablePitchersSlugs } from '@sorare/core/src/hooks/config/useNextFixtureProbablePitchersSlugs';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

const FILTER = VIRTUAL_TOGGLE_FILTERS.probablePitchersFilter;

const ProbablePitchersFilter = ({ visible }: { visible: boolean }) => {
  const { probablePitchersForNextGameWeeks = [] } =
    useNextFixtureProbablePitchersSlugs(false);
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });
  const track = useEvents();
  const onChange = useCallback(
    (pp: string) => {
      const isActive = currentRefinement === pp;
      const newValue = isActive ? undefined : pp;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: pp,
        });
      }
    },
    [currentRefinement, setVirtualToggle, track]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {probablePitchersForNextGameWeeks
        .slice(0, 3)
        .map(({ gameWeek, longDisplayName }) => (
          <Option
            key={gameWeek}
            active={currentRefinement === gameWeek.toString()}
            onClick={() => onChange(gameWeek.toString())}
            variant="radio"
          >
            {longDisplayName}
          </Option>
        ))}
    </FilterSection>
  );
};

const Title = () => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];

  return <FilterTitle name={FILTER.title!} selected={selected} />;
};

export const widget: FilterWidget = {
  key: FILTER.name,
  type: 'virtualToggle',
  filter: FILTER,
  Component: ({ visible }) => <ProbablePitchersFilter visible={visible} />,
  title: <Title />,
};
